import React, { useState, useEffect, useMemo } from "react";
import {
  Input,
  Row,
  Card,
  Table,
  Spin,
  Button,
  Space,
  Form,
  Pagination,
} from "antd";
import { ArrowDownOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { connect, useDispatch } from "react-redux";

import MainLayout from "../../../components/layouts/enhancement/MainLayout";
import TopContent from "../../../components/layouts/enhancement/TopContent";
import cnt from "../../../services/definedConstant";
import { rightAction } from "../../../redux/actions";
import {
  labelConfig,
  staticConfig,
  apiConfig,
} from "../treasuryContractBooking.config";
import SearchLg from "@untitled-ui/icons-react/build/esm/SearchLg";
import ArrowLeft from "@untitled-ui/icons-react/build/esm/ArrowLeft";
import ArrowRight from "@untitled-ui/icons-react/build/esm/ArrowRight";
import { BOM } from "../../../services/definedMethod";

// ── Dummy data (5 records) ─────────────────────────────────────────────────
const DUMMY_TRADES = [
  {
    id: 1,
    tradeDate: "2026-02-22",
    settlementDate: "2026-02-24",
    maturityDate: "2026-05-25",
    faceValue: 50_000_000.0,
    tradePrice: 98.5,
    considerationAmount: 49_250_000.0,
    status: "CONFIRMED",
    portfolioId: 1,
    securityId: 1,
  },
  {
    id: 2,
    tradeDate: "2026-03-01",
    settlementDate: "2026-03-03",
    maturityDate: "2026-06-01",
    faceValue: 75_000_000.0,
    tradePrice: 97.8,
    considerationAmount: 73_350_000.0,
    status: "SETTLED",
    portfolioId: 2,
    securityId: 2,
  },
  {
    id: 3,
    tradeDate: "2026-03-08",
    settlementDate: "2026-03-10",
    maturityDate: "2026-09-07",
    faceValue: 100_000_000.0,
    tradePrice: 96.5,
    considerationAmount: 96_500_000.0,
    status: "PENDING",
    portfolioId: 1,
    securityId: 3,
  },
  {
    id: 4,
    tradeDate: "2026-03-15",
    settlementDate: "2026-03-17",
    maturityDate: "2026-06-15",
    faceValue: 200_000_000.0,
    tradePrice: 99.1,
    considerationAmount: 198_200_000.0,
    status: "CONFIRMED",
    portfolioId: 3,
    securityId: 1,
  },
  {
    id: 5,
    tradeDate: "2026-03-22",
    settlementDate: "2026-03-24",
    maturityDate: "2026-12-21",
    faceValue: 30_000_000.0,
    tradePrice: 95.0,
    considerationAmount: 28_500_000.0,
    status: "CANCELLED",
    portfolioId: 2,
    securityId: 4,
  },
];

const STATUS_COLORS = {
  CONFIRMED: { bg: "#ECFDF3", color: "#027A48" },
  SETTLED: { bg: "#EFF8FF", color: "#175CD3" },
  PENDING: { bg: "#FFFAEB", color: "#B54708" },
  CANCELLED: { bg: "#FEF3F2", color: "#B42318" },
};

const StatusBadge = ({ status }) => {
  const s = STATUS_COLORS[status] || { bg: "#F2F4F7", color: "#344054" };
  return (
    <span
      style={{
        display: "inline-block",
        padding: "2px 10px",
        borderRadius: 99,
        fontSize: 12,
        fontWeight: 600,
        background: s.bg,
        color: s.color,
      }}
    >
      {status}
    </span>
  );
};

const fmt = (val) =>
  val != null ? val.toLocaleString("en-NG", { minimumFractionDigits: 2 }) : "-";

// ── Component ──────────────────────────────────────────────────────────────
const ManageTrade = (props) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const { rightBtn, rightAction } = props;

  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRow, setSelectedRow] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [originalList, setOriginalList] = useState([]);
  const [loading, setLoading] = useState(false);

  const pageSize = 10;

  const columns = [
    {
      title: (
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          Trade Date{" "}
          <ArrowDownOutlined style={{ fontSize: 14, color: "gray" }} />
        </div>
      ),
      dataIndex: "tradeDate",
      sorter: (a, b) => a.tradeDate.localeCompare(b.tradeDate),
    },
    { title: "Settlement Date", dataIndex: "settlementDate" },
    { title: "Maturity Date", dataIndex: "maturityDate" },
    { title: "Portfolio ID", dataIndex: "portfolioId" },
    { title: "Security ID", dataIndex: "securityId" },
    {
      title: "Face Value",
      dataIndex: "faceValue",
      render: fmt,
    },
    {
      title: "Trade Price",
      dataIndex: "tradePrice",
      render: (val) => (val != null ? val.toFixed(2) : "-"),
    },
    {
      title: "Consideration Amount",
      dataIndex: "considerationAmount",
      render: fmt,
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (val) => <StatusBadge status={val} />,
    },
  ];

  useEffect(() => {
    getTradeList();
  }, []);

  useEffect(() => {
    if (BOM.HasPermission?.("CREATE_TRADE")) {
      rightAction(cnt.RIGHT_ADD_BTN);
    }
  }, []);

  const getTradeList = () => {
    setLoading(true);

    // ── Swap for real API when ready ──
    // BOM.FetchReqAction({ session }, apiConfig.GetTrades, (err, res) => { ... });

    setTimeout(() => {
      const mapped = DUMMY_TRADES.map((el) => ({ key: el.id, ...el }));
      setOriginalList(mapped);
      setSelectedRow([]);
      setCurrentPage(1);
      setLoading(false);
    }, 400);
  };

  const handleSearchInTable = (e) => {
    setSearchTerm(e.target.value || "");
    setCurrentPage(1);
  };

  const filteredData = useMemo(() => {
    if (!searchTerm) return originalList;
    const q = searchTerm.toLowerCase();
    return originalList.filter(
      (item) =>
        item.tradeDate?.toLowerCase().includes(q) ||
        item.status?.toLowerCase().includes(q) ||
        String(item.portfolioId).includes(q) ||
        String(item.securityId).includes(q),
    );
  }, [originalList, searchTerm]);

  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredData.slice(start, start + pageSize);
  }, [filteredData, currentPage]);

  const totalPages = Math.ceil(filteredData.length / pageSize);

  const itemRender = (_, type, el) => {
    if (type === "prev" || type === "next") return;
    return el;
  };

  const onSelectItemAction = (selectedRowKeys, selectedRows) => {
    if (!selectedRowKeys || selectedRowKeys.length !== 1) {
      setSelectedRow([]);
      dispatch({ type: cnt.CLEAR_RIGHT_BTN });
      if (BOM.HasPermission?.("CREATE_TRADE")) {
        dispatch({ type: cnt.RIGHT_ADD_BTN });
      }
      return;
    }
    setSelectedRow(selectedRows);
    if (BOM.HasPermission?.("READ_TRADE"))
      dispatch({ type: cnt.RIGHT_DETAIL_BTN });
    if (BOM.HasPermission?.("UPDATE_TRADE"))
      dispatch({ type: cnt.RIGHT_EDIT_BTN });
  };

  const handleOnView = () => {
    if (rightBtn?.detailBtn === "primary")
      navigate("/view-trade", { state: selectedRow[0]?.id });
  };
  const handleOnCreate = () => {
    if (rightBtn?.addBtn === "primary") navigate("/add-trade");
  };
  const handleOnEdit = () => {
    if (rightBtn?.editBtn === "primary")
      navigate("/edit-trade", { state: selectedRow[0]?.id });
  };

  return (
    <MainLayout
      isEnableRightSidebar
      onView={handleOnView}
      onAdd={handleOnCreate}
      onEdit={handleOnEdit}
      pageItems={staticConfig.manageTrade}
    >
      <TopContent
        pageTitle={labelConfig.trade}
        createPageTitle={labelConfig.tradeListing}
      />

      <Card
        style={{ border: "1px solid #EAECF0", borderRadius: 20, marginTop: 12 }}
      >
        <Spin spinning={loading}>
          <Row
            style={{
              marginBottom: 20,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Form form={form}>
              <Space size={12}>
                <Input
                  value={searchTerm}
                  onChange={handleSearchInTable}
                  placeholder="Search trades…"
                  prefix={<SearchLg width={18} color="#667085" />}
                  style={{ width: 350, borderRadius: 10 }}
                />
              </Space>
            </Form>
          </Row>

          <Table
            rowSelection={{
              type: "checkbox",
              hideSelectAll: true,
              onChange: onSelectItemAction,
            }}
            columns={columns}
            dataSource={paginatedData}
            pagination={false}
          />

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginTop: 20,
            }}
          >
            <Button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
            >
              <ArrowLeft /> Previous
            </Button>

            <Pagination
              current={currentPage}
              total={filteredData.length}
              pageSize={pageSize}
              showSizeChanger={false}
              itemRender={itemRender}
              onChange={setCurrentPage}
            />

            <Button
              disabled={currentPage >= totalPages}
              onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
            >
              Next <ArrowRight />
            </Button>
          </div>
        </Spin>
      </Card>
    </MainLayout>
  );
};

const mapStateToProps = (state) => ({ rightBtn: state.RightSideBtn });
export default connect(mapStateToProps, { rightAction })(ManageTrade);
